import { useState, useRef, useEffect } from "react";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import Avatar from "./Avatar";
import styles from "./AvatarUploadField.module.css";

function UploadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 9V1M4 4l3-3 3 3M2 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Preview({ src, shape }) {
  if (shape === "circle") {
    return <Avatar src={src} name="" size={40} objectFit="cover" />;
  }
  return (
    <img
      src={src}
      alt=""
      style={{ width: 40, height: 40, objectFit: "contain", borderRadius: 8, display: "block" }}
    />
  );
}

export default function AvatarUploadField({ src, isEditing, onChange, onDelete, shape = "square" }) {
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const fileRef = useRef(null);
  const activeIntervalRef = useRef(null);
  const pendingUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (activeIntervalRef.current) clearInterval(activeIntervalRef.current);
      if (pendingUrlRef.current) URL.revokeObjectURL(pendingUrlRef.current);
    };
  }, []);

  const currentSrc = preview ?? src ?? null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setShowPicker(false);
    setProgress(0);

    if (activeIntervalRef.current) clearInterval(activeIntervalRef.current);
    if (pendingUrlRef.current) URL.revokeObjectURL(pendingUrlRef.current);

    const url = URL.createObjectURL(file);
    pendingUrlRef.current = url;

    let pct = 0;
    const iv = setInterval(() => {
      pct += Math.random() * 25 + 5;
      if (pct >= 100) {
        clearInterval(iv);
        activeIntervalRef.current = null;
        pendingUrlRef.current = null;
        setProgress(null);
        setPreview(url);
        onChange?.(file, url);
      } else {
        setProgress(Math.floor(pct));
      }
    }, 150);
    activeIntervalRef.current = iv;
  };

  const handleDelete = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setProgress(null);
    setShowPicker(false);
    onDelete?.();
  };

  if (!isEditing) {
    if (!currentSrc) return <span style={{ fontSize: 14, color: "var(--text-primary)" }}>-</span>;
    return <Preview src={currentSrc} shape={shape} />;
  }

  if (progress !== null) {
    return (
      <div className={styles.loadingRow}>
        <div className={styles.spinner} />
        <span className={styles.progress}>{progress}%</span>
      </div>
    );
  }

  if (currentSrc) {
    return (
      <div className={styles.hasImage}>
        <Preview src={currentSrc} shape={shape} />
        <div className={styles.actions}>
          <button type="button" className={styles.adjuntarBtn} onClick={() => fileRef.current?.click()}>
            <UploadIcon />
            Cambiar imagen
          </button>
          <button type="button" className={styles.deleteBtn} onClick={handleDelete} aria-label="Eliminar imagen">
            <TrashIcon className={styles.trashIcon} />
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
      </div>
    );
  }

  return (
    <div className={styles.emptyWrapper}>
      <button type="button" className={styles.emptyText} onClick={() => setShowPicker(true)}>
        Agrega una imagen
      </button>
      {showPicker && (
        <button type="button" className={styles.adjuntarBtn} onClick={() => fileRef.current?.click()}>
          <UploadIcon />
          Adjuntar una imagen
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
    </div>
  );
}
